import React, { Component } from 'react';
import axios from 'axios';

class TrainingShow extends Component {
    constructor(props) {
        super(props);
        this.state = {
            training: null,
            relatedTrainings: [],
            loading: true,
            error: null,
            expandedFaq: null,
            activeFaqCategory: null,
            currentTestimonialIndex: 0,
            currentRelatedIndex: 0,
        };
        this._isMounted = false;
        this.trainingController = null;
        this.relatedController = null;
    }

    componentDidMount() {
        this._isMounted = true;
        const slug = this.getSlugFromURL();
        if (slug) {
            this.fetchTrainingDetail(slug);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.trainingController) this.trainingController.abort();
        if (this.relatedController) this.relatedController.abort();
    }

    getSlugFromURL = () => {
        // Get slug from URL path (e.g., /training/web-development)
        const pathArray = window.location.pathname.split('/');
        return pathArray[pathArray.length - 1];
    }

    fetchTrainingDetail = (slug) => {
        const token = localStorage.getItem('backend-jwt-token');
        this.trainingController = new AbortController();

        axios.post('/api/trainings/show', { slug }, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            signal: this.trainingController.signal
        })
        .then(response => {
            if (!this._isMounted) return;
            if (response.data.status === 'success' && response.data.data) {
                const trainingData = response.data.data;

                // Set initial active FAQ category to the first available category
                let initialFaqCategory = null;
                if (trainingData.faqs && trainingData.faqs.length > 0) {
                    const firstFaq = trainingData.faqs[0];
                    initialFaqCategory = firstFaq.faq_category_id || 'uncategorized';
                }

                this.setState({
                    training: trainingData,
                    loading: false,
                    activeFaqCategory: initialFaqCategory,
                }, () => {
                    this.fetchRelatedTrainings(trainingData.category_id, trainingData.id);
                });
            } else if (response.data.code === 404) {
                this.setState({
                    error: 'Training not found',
                    loading: false
                });
            }
        })
        .catch(error => {
            if (!this._isMounted) return;
            if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
                console.error('Error fetching training:', error);
                this.setState({
                    error: 'Failed to load training details',
                    loading: false
                });
            }
        });
    }

    fetchRelatedTrainings = (categoryId, currentTrainingId) => {
        const token = localStorage.getItem('backend-jwt-token');
        this.relatedController = new AbortController();

        axios.get('/api/trainings/frontend-list', {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            signal: this.relatedController.signal
        })
        .then(response => {
            if (!this._isMounted) return;
            if (response.data.data) {
                // Filter related trainings: same category, exclude current training
                const related = response.data.data.filter(t =>
                    t.category_id === categoryId && t.id !== currentTrainingId
                );

                this.setState({ relatedTrainings: related });
            }
        })
        .catch(error => {
            if (!this._isMounted) return;
            if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
                console.error('Error fetching related trainings:', error);
            }
        });
    }

    toggleFaq = (index) => {
        this.setState({
            expandedFaq: this.state.expandedFaq === index ? null : index
        });
    }

    handleFaqCategoryChange = (categoryId) => {
        this.setState({
            activeFaqCategory: categoryId,
            expandedFaq: null,
        });
    }

    handleRelatedPrev = () => {
        const { currentRelatedIndex } = this.state;
        const totalPages = this.getRelatedTotalPages();
        const newIndex = currentRelatedIndex <= 0 ? totalPages - 1 : currentRelatedIndex - 1;
        this.setState({ currentRelatedIndex: newIndex });
    }

    handleRelatedNext = () => {
        const { currentRelatedIndex } = this.state;
        const totalPages = this.getRelatedTotalPages();
        const newIndex = currentRelatedIndex >= totalPages - 1 ? 0 : currentRelatedIndex + 1;
        this.setState({ currentRelatedIndex: newIndex });
    }

    handleRelatedDotClick = (index) => {
        this.setState({ currentRelatedIndex: index });
    }

    getRelatedTotalPages = () => {
        const { relatedTrainings } = this.state;
        const perPage = 4;
        return Math.max(1, Math.ceil(relatedTrainings.length / perPage));
    }

    handleTestimonialPrev = () => {
        const { training, currentTestimonialIndex } = this.state;
        if (!training || !training.testimonials) return;
        const totalPages = Math.max(1, Math.ceil(training.testimonials.length / 3));
        const newIndex = currentTestimonialIndex <= 0 ? totalPages - 1 : currentTestimonialIndex - 1;
        this.setState({ currentTestimonialIndex: newIndex });
    }

    handleTestimonialNext = () => {
        const { training, currentTestimonialIndex } = this.state;
        if (!training || !training.testimonials) return;
        const totalPages = Math.max(1, Math.ceil(training.testimonials.length / 3));
        const newIndex = currentTestimonialIndex >= totalPages - 1 ? 0 : currentTestimonialIndex + 1;
        this.setState({ currentTestimonialIndex: newIndex });
    }

    handleEnquirySubmit = (e) => {
        e.preventDefault();
        // TODO: wire this up to backend enquiry endpoint
        console.log('Enquiry form submitted - wire to API');
    }

    renderStars = (rating) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <i key={i} className={`fas fa-star ${i < rating ? '' : 'text-muted'}`}></i>
            );
        }
        return stars;
    }

    groupFaqsByCategory = () => {
        const { training } = this.state;
        if (!training || !training.faqs || training.faqs.length === 0) {
            return {};
        }

        const grouped = {};

        training.faqs.forEach(faq => {
            const categoryId = faq.faq_category_id || 'uncategorized';
            const categoryName = faq.faq_category ? faq.faq_category.name : 'General';

            if (!grouped[categoryId]) {
                grouped[categoryId] = {
                    name: categoryName,
                    faqs: []
                };
            }
            grouped[categoryId].faqs.push(faq);
        });

        return grouped;
    }

    render() {
        const {
            training,
            relatedTrainings,
            loading,
            error,
            expandedFaq,
            activeFaqCategory,
            currentTestimonialIndex,
            currentRelatedIndex,
        } = this.state;

        if (loading) {
            return (
                <div className="training-detail-page">
                    <div className="loading-spinner">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p>Loading training details...</p>
                    </div>
                </div>
            );
        }

        if (error || !training) {
            return (
                <div className="training-detail-page">
                    <div className="container py-5">
                        <div className="not-found-container">
                            <div className="not-found-icon">
                                <i className="fas fa-search"></i>
                            </div>
                            <h1 className="not-found-title">Training Not Found</h1>
                            <p className="not-found-message">
                                {error || 'The training page you are looking for does not exist.'}
                            </p>
                            <p className="not-found-subtitle">
                                It might have been removed or the link might be broken.
                            </p>
                            <div className="not-found-actions">
                                <a href="/trainings" className="btn btn-primary btn-lg">
                                    <i className="fas fa-arrow-left"></i> Back to Trainings
                                </a>
                                <a href="/" className="btn btn-outline-primary btn-lg">
                                    <i className="fas fa-home"></i> Go Home
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Grouped FAQs and active category data
        const groupedFaqs = this.groupFaqsByCategory();
        const faqCategoryIds = Object.keys(groupedFaqs);
        const activeCategoryData = activeFaqCategory && groupedFaqs[activeFaqCategory]
            ? groupedFaqs[activeFaqCategory]
            : null;

        // Testimonials pagination (3 per page)
        const testimonials = training.testimonials || [];
        const testimonialsPerPage = 3;
        const totalTestimonialPages = Math.max(1, Math.ceil(testimonials.length / testimonialsPerPage));
        const visibleTestimonials = testimonials.slice(
            currentTestimonialIndex * testimonialsPerPage,
            currentTestimonialIndex * testimonialsPerPage + testimonialsPerPage
        );

        // Related trainings pagination (4 per page)
        const relatedPerPage = 4;
        const totalRelatedPages = Math.max(1, Math.ceil(relatedTrainings.length / relatedPerPage));
        const visibleRelated = relatedTrainings.slice(
            currentRelatedIndex * relatedPerPage,
            currentRelatedIndex * relatedPerPage + relatedPerPage
        );

        return (
            <div className="training-detail-page">
                {/* ============ Page Title Bar ============ */}
                <section className="td-title-bar">
                    <div className="container">
                        <h1 className="td-title">{training.title}</h1>
                    </div>
                </section>

                {/* ============ Intro / Hero Two-Column ============ */}
                <section className="td-intro-section">
                    <div className="container">
                        <div className="td-intro-wrapper">
                            <div className="td-intro-left">
                                {training.tagline && (
                                    <p className="td-intro-tagline">{training.tagline}</p>
                                )}
                                <h3 className="td-intro-subhead">Course Overview</h3>
                                <div className="td-intro-body">
                                    {training.course_overview && (
                                        <p>{training.course_overview}</p>
                                    )}
                                </div>
                                <div className="td-intro-buttons">
                                    <button className="btn btn-td-primary">Enroll Now</button>
                                    <a href={`/uploads/trainings/${training.brochure_file}`} className="btn btn-td-outline" target="_blank" rel="noopener noreferrer">
                                        Download Brochure
                                    </a>
                                </div>
                            </div>
                            <div className="td-intro-right">
                                {training.banner_image ? (
                                    <img
                                        src={`/uploads/trainings/${training.banner_image}`}
                                        alt={training.title}
                                        className="td-intro-image"
                                    />
                                ) : (
                                    <div className="td-intro-image-placeholder">
                                        <i className="fas fa-image"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ Course Overview Info Strip ============ */}
                <section className="td-info-strip">
                    <div className="container">
                        <h2 className="td-section-title">Course Overview</h2>
                        <div className="td-info-grid">
                            {training.who_should_attend && (
                                <div className="td-info-item">
                                    <div className="td-info-icon">
                                        <i className="fas fa-users"></i>
                                    </div>
                                    <h5>Who Should Attend</h5>
                                    <p>{training.who_should_attend}</p>
                                </div>
                            )}
                            {training.duration && (
                                <div className="td-info-item">
                                    <div className="td-info-icon">
                                        <i className="fas fa-hourglass-half"></i>
                                    </div>
                                    <h5>Duration</h5>
                                    <p>{training.duration}</p>
                                </div>
                            )}
                            {training.start_date_bs && (
                                <div className="td-info-item">
                                    <div className="td-info-icon">
                                        <i className="fas fa-calendar"></i>
                                    </div>
                                    <h5>Start Date</h5>
                                    <p>{training.start_date_bs}</p>
                                </div>
                            )}
                            {training.training_mode && (
                                <div className="td-info-item">
                                    <div className="td-info-icon">
                                        <i className="fas fa-laptop"></i>
                                    </div>
                                    <h5>Training Mode</h5>
                                    <p className="text-capitalize">{training.training_mode}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ============ Career Paths ============ */}
                {training.career_paths && training.career_paths.length > 0 && (
                    <section className="td-careers-section">
                        <div className="container">
                            <h2 className="td-section-title">Career Path After This Training</h2>
                            <div className="td-careers-grid">
                                {training.career_paths.map((path, index) => (
                                    <div key={index} className="td-career-card">
                                        <div className="td-career-icon">
                                            {path.icon ? (
                                                <i className={`fas fa-${path.icon}`}></i>
                                            ) : (
                                                <i className="fas fa-briefcase"></i>
                                            )}
                                        </div>
                                        <h5>{path.career_title}</h5>
                                        {path.description && <p>{path.description}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* ============ Outcomes + Modules + Enquiry Sidebar ============ */}
                <section className="td-main-section">
                    <div className="container">
                        <div className="td-main-wrapper">
                            <div className="td-main-left">
                                {/* Outcomes Box */}
                                {training.outcomes && training.outcomes.length > 0 && (
                                    <div className="td-outcomes-box">
                                        <div className="td-box-header">
                                            <h3>Learning Outcomes &amp; Benefits &ndash; Participants Will Gain</h3>
                                        </div>
                                        <ul className="td-outcomes-list">
                                            {training.outcomes.map((outcome, index) => (
                                                <li key={index}>
                                                    <i className="fas fa-check-circle"></i>
                                                    <span>{outcome.outcome_text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Modules Accordion */}
                                {training.modules && training.modules.length > 0 && (
                                    <div className="td-modules-box">
                                        <div className="td-box-header td-box-header-flex">
                                            <h3>{training.title} Modules</h3>
                                        </div>
                                        <div className="td-modules-subbar">
                                            <span>Course | {training.duration || ''}</span>
                                            <a href="#" className="td-modules-expand">Expand All Course</a>
                                        </div>
                                        <div className="td-modules-list">
                                            {training.modules.map((module, index) => {
                                                const uniqueKey = `mod-${index}`;
                                                const isExpanded = expandedFaq === uniqueKey;
                                                return (
                                                    <div key={uniqueKey} className={`td-module-item ${isExpanded ? 'expanded' : ''}`}>
                                                        <div
                                                            className="td-module-header"
                                                            onClick={() => this.toggleFaq(uniqueKey)}
                                                        >
                                                            <span className="td-module-title">{module.module_title}</span>
                                                            <i className={`fas fa-chevron-down td-chevron ${isExpanded ? 'rotated' : ''}`}></i>
                                                        </div>
                                                        {isExpanded && module.module_content && (
                                                            <div className="td-module-content">
                                                                <p>{module.module_content}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sidebar Enquiry */}
                            <aside className="td-main-right">
                                <div className="td-sidebar-buttons">
                                    <button className="btn btn-td-primary btn-block">Enroll Now</button>
                                    {/* <button className="btn btn-td-outline btn-block">Download Brochure</button> */}
                                    <a href={`/uploads/trainings/${training.brochure_file}`} className="btn btn-td-outline" target="_blank" rel="noopener noreferrer">
                                        Download Brochure
                                    </a>
                                </div>
                                <div className="td-enquiry-box">
                                    <h4>Send Enquiry</h4>
                                    <form onSubmit={this.handleEnquirySubmit}>
                                        <div className="td-form-group">
                                            <label>Full Name *</label>
                                            <input type="text" placeholder="Please Enter Your Full Name" />
                                        </div>
                                        <div className="td-form-group">
                                            <label>Email *</label>
                                            <input type="email" placeholder="Please Enter Your Email" />
                                        </div>
                                        <div className="td-form-group">
                                            <label>Phone Number *</label>
                                            <input type="text" placeholder="Please Enter Your Phone Number" />
                                        </div>
                                        <div className="td-form-group">
                                            <label>How may we assist You? *</label>
                                            <textarea rows="4" placeholder="Write your message..."></textarea>
                                        </div>
                                        <button type="submit" className="btn btn-td-primary btn-block td-form-submit">
                                            Send Message
                                        </button>
                                    </form>
                                </div>
                            </aside>
                        </div>
                    </div>
                </section>

                {/* ============ Meet Your Trainer(s) ============ */}
                {training.trainers && training.trainers.length > 0 && (
                    <section className="td-trainers-section">
                        <div className="container">
                            <h2 className="td-section-title">Meet Your Trainer</h2>
                            <div className="td-trainers-list">
                                {training.trainers.map((trainer, index) => (
                                    <div key={index} className="td-trainer-card">
                                        <div className="td-trainer-photo-wrap">
                                            {trainer.photo ? (
                                                <img
                                                    src={`/uploads/emp_img/${trainer.photo}`}
                                                    alt={trainer.name}
                                                    className="td-trainer-photo"
                                                />
                                            ) : (
                                                <div className="td-trainer-photo td-trainer-photo-placeholder">
                                                    <i className="fas fa-user"></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="td-trainer-info">
                                            <h4 className="td-trainer-name">{trainer.name}</h4>
                                            {trainer.designation && (
                                                <p className="td-trainer-designation">{trainer.designation}</p>
                                            )}
                                            {trainer.qualification && (
                                                <p className="td-trainer-qualification">{trainer.qualification}</p>
                                            )}
                                            {trainer.bio && (
                                                <p className="td-trainer-bio">{trainer.bio}</p>
                                            )}
                                            <div className="td-trainer-social">
                                                {trainer.facebook_url && (
                                                    <a href={trainer.facebook_url} target="_blank" rel="noopener noreferrer" title="Facebook">
                                                        <i className="fab fa-facebook"></i>
                                                    </a>
                                                )}
                                                {trainer.twitter_url && (
                                                    <a href={trainer.twitter_url} target="_blank" rel="noopener noreferrer" title="Twitter">
                                                        <i className="fab fa-twitter"></i>
                                                    </a>
                                                )}
                                                {trainer.linkedin_url && (
                                                    <a href={trainer.linkedin_url} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                                                        <i className="fab fa-linkedin"></i>
                                                    </a>
                                                )}
                                                {trainer.instagram_url && (
                                                    <a href={trainer.instagram_url} target="_blank" rel="noopener noreferrer" title="Instagram">
                                                        <i className="fab fa-instagram"></i>
                                                    </a>
                                                )}
                                                {trainer.whatsapp_number && (
                                                    <a href={`https://wa.me/${trainer.whatsapp_number}`} target="_blank" rel="noopener noreferrer" title="WhatsApp">
                                                        <i className="fab fa-whatsapp"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* ============ Explore Other Training Programs (Carousel) ============ */}
                {relatedTrainings.length > 0 && (
                    <section className="td-related-section">
                        <div className="container">
                            <h2 className="td-section-title">Explore Other Training Programs</h2>
                            <div className="td-related-carousel">
                                <div className="td-related-grid">
                                    {visibleRelated.map((rel, index) => (
                                        <div key={index} className="td-related-card">
                                            {rel.banner_image ? (
                                                <img
                                                    src={`/uploads/trainings/${rel.banner_image}`}
                                                    alt={rel.title}
                                                    className="td-related-image"
                                                />
                                            ) : (
                                                <div className="td-related-image td-related-image-placeholder">
                                                    <i className="fas fa-image"></i>
                                                </div>
                                            )}
                                            <div className="td-related-body">
                                                <h5>{rel.title}</h5>
                                                {rel.duration && (
                                                    <p className="td-related-duration">
                                                        <i className="far fa-clock"></i> {rel.duration}
                                                    </p>
                                                )}
                                                <div className="td-related-buttons">
                                                    <a
                                                        href={`/training/${rel.slug}`}
                                                        className="btn btn-td-outline btn-sm"
                                                    >
                                                        View Details
                                                    </a>
                                                    <button
                                                        className="btn btn-td-primary btn-sm"
                                                        onClick={() => alert('Enrollment functionality coming soon!')}
                                                    >
                                                        Enroll Now
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {totalRelatedPages > 1 && (
                                    <div className="td-carousel-dots">
                                        {Array.from({ length: totalRelatedPages }).map((_, idx) => (
                                            <span
                                                key={idx}
                                                className={`td-dot ${idx === currentRelatedIndex ? 'active' : ''}`}
                                                onClick={() => this.handleRelatedDotClick(idx)}
                                            ></span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* ============ Feedback From Our Students ============ */}
                {testimonials.length > 0 && (
                    <section className="td-feedback-section">
                        <div className="container">
                            <h2 className="td-section-title">FeedBack From Our Students</h2>
                            <div className="td-feedback-grid">
                                {visibleTestimonials.map((testimonial, index) => (
                                    <div key={index} className="td-feedback-card">
                                        {testimonial.student_photo ? (
                                            <img
                                                src={`/uploads/trainings/testimonials/${testimonial.student_photo}`}
                                                alt={testimonial.student_name}
                                                className="td-feedback-avatar"
                                            />
                                        ) : (
                                            <div className="td-feedback-avatar td-feedback-avatar-placeholder">
                                                <i className="fas fa-user"></i>
                                            </div>
                                        )}
                                        <h6 className="td-feedback-name">{testimonial.student_name}</h6>
                                        <p className="td-feedback-designation">{testimonial.designation}</p>
                                        <p className="td-feedback-text">{testimonial.testimonial_text}</p>
                                        <div className="td-feedback-rating">
                                            {this.renderStars(testimonial.rating || 5)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {totalTestimonialPages > 1 && (
                                <div className="td-carousel-arrows">
                                    <button
                                        className="td-arrow-btn"
                                        onClick={this.handleTestimonialPrev}
                                        aria-label="Previous"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    <button
                                        className="td-arrow-btn"
                                        onClick={this.handleTestimonialNext}
                                        aria-label="Next"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* ============ FAQ Section ============ */}
                {training.faqs && training.faqs.length > 0 && (
                    <section className="td-faq-section">
                        <div className="container">
                            <h2 className="td-section-title">FAQ - Frequently Asked Questions</h2>
                            <p className="td-faq-subtitle">Everything you need to know about the program</p>

                            {faqCategoryIds.length > 1 && (
                                <div className="td-faq-tabs">
                                    {faqCategoryIds.map(catId => (
                                        <button
                                            key={catId}
                                            className={`td-faq-tab ${activeFaqCategory === catId ? 'active' : ''}`}
                                            onClick={() => this.handleFaqCategoryChange(catId)}
                                        >
                                            {groupedFaqs[catId].name}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="td-faq-list">
                                {activeCategoryData && activeCategoryData.faqs.map((faq, index) => {
                                    const uniqueKey = `faq-${activeFaqCategory}-${index}`;
                                    const isExpanded = expandedFaq === uniqueKey;
                                    return (
                                        <div key={uniqueKey} className={`td-faq-item ${isExpanded ? 'expanded' : ''}`}>
                                            <div
                                                className="td-faq-question"
                                                onClick={() => this.toggleFaq(uniqueKey)}
                                            >
                                                <span>{faq.question}</span>
                                                <i className={`fas fa-chevron-down td-chevron ${isExpanded ? 'rotated' : ''}`}></i>
                                            </div>
                                            {isExpanded && (
                                                <div className="td-faq-answer">
                                                    {faq.answer}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}
            </div>
        );
    }
}

export default TrainingShow;